import * as PIXI from 'pixi.js';
import { Config } from '../../video-data/config';

import {
    NumberAttribute,
    ColorAttribute,
    AlphaAttribute,
    getAttributesToSerialize,
} from '../attributes';
import { DynamicStylingMixin } from '../dynamicStylingMixin';
import type { BaseAssetSettings } from './BaseAsset';
import { BaseAsset } from './BaseAsset';

/**
 * A RectangleAsset Object will load the specified solid color.
 */
export class RectangleAsset extends DynamicStylingMixin(BaseAsset) {
    override _classType = Config.RECTANGLE_ASSET_TYPE;

    _graphics: PIXI.Graphics;

    @NumberAttribute({ min: 0 })
    accessor rectWidth = 10;
    @NumberAttribute({ min: 0 })
    accessor rectHeight = 10;
    @NumberAttribute({ min: 0 })
    accessor lineThickness = 0;
    @ColorAttribute()
    accessor lineColor = 16777215;
    @ColorAttribute()
    accessor color = 16777215;
    @AlphaAttribute()
    accessor fillAlpha = 1;
    @NumberAttribute({ min: 0, max: 1, serialize: false })
    accessor lineAlpha = 1;
    @NumberAttribute({ min: 0, max: 1, serialize: false })
    accessor lineAlignment = 0.5;

    constructor(container: PIXI.Container, settings: BaseAssetSettings) {
        super(container, settings);

        this._graphics = new PIXI.Graphics();
        this._addInternalChild(this._graphics);
    }

    /**
     * This asset doesn't have to load anything remotely so it should "always load"
     */
    override get shouldLoad() {
        return true;
    }

    override isMorphMatch(other: BaseAsset): boolean {
        const otherRect = other as RectangleAsset;
        if (!Config.isRectangleAsset(otherRect)) {
            return false;
        }

        if (this.color !== otherRect.color) {
            return false;
        }

        const samePosition =
            this.transformation.position.x === otherRect.transformation.position.x &&
            this.transformation.position.y === otherRect.transformation.position.y;
        const sameSize =
            this.rectWidth === otherRect.rectWidth && this.rectHeight === otherRect.rectHeight;
        const sameAlpha = this.alpha === otherRect.alpha;

        if (samePosition && sameSize && sameAlpha) {
            return false;
        }

        return true;
    }

    protected override _build() {
        this.clearAnimation();

        if (this.hasDynamicStyle || this.hasMorphAnimation) {
            this._prepareDynamicStylingForUpdate();
            this.applyTransformation();
            this.applyAnimation();
            this._updateDynamicStyling();
        } else {
            this.applyTransformation();
            this.applyAnimation();
        }

        this.applyIntroAnimations();
        this.applyOutroAnimations();
        this.animationManager.setInitValues();
    }

    override _draw() {
        this._graphics.clear();
        this._graphics.lineStyle(
            this.lineThickness,
            this.lineColor,
            this.lineAlpha,
            this.lineAlignment,
        );
        this._graphics.beginFill(this.color, this.fillAlpha);
        this._graphics.drawRect(0, 0, this.rectWidth, this.rectHeight);
        this._graphics.endFill();

        this.applyTransformation();
    }

    override get propertiesToSerialize() {
        return super.propertiesToSerialize.concat(['mask'], getAttributesToSerialize(this));
    }
}
